import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { createInterviewSlot, createCompanyJobDescription, createCompanyProfile } from '../graphql/mutations';
import { listCompanyProfiles } from '../graphql/queries';
import { createMeetingConfig, findOrCreateCandidateProfile } from '../utils/candidateUtils';
import { RiseLoader } from "react-spinners";

const DemoInterviewLanding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: 'Frontend Developer'
  });

  const roles = [
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Data Scientist",
    "Product Manager",
    "DevOps Engineer"
  ];

  const handleStartDemo = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Please enter your name");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Create Candidate Profile (Temporary)
      const dummyEmail = `demo-${Date.now()}@demo.getscreened.ai`;
      const { candidateProfileID } = await findOrCreateCandidateProfile(formData.name, dummyEmail);

      // 2. Find or Create Demo Company
      let companyId = await getOrCreateDemoCompany();

      // 3. Create JD
      const jdId = await createDemoJD(companyId, formData.role);

      // 4. Create Interview Slot
      const slotId = await createDemoInterviewSlot(companyId, jdId, candidateProfileID, formData.name);

      // 5. Redirect
      navigate(`/public-interview/${slotId}`);

    } catch (err) {
      console.error("Error starting demo:", err);
      setError("Failed to start demo interview. Please try again.");
      setLoading(false);
    }
  };

  const getOrCreateDemoCompany = async () => {
    const demoEmail = "demo@getscreened.ai";
    // List companies
    const listRes = await API.graphql(graphqlOperation(listCompanyProfiles, {
      filter: { email: { eq: demoEmail } }
    }));

    if (listRes.data.listCompanyProfiles.items.length > 0) {
      return listRes.data.listCompanyProfiles.items[0].id;
    }

    // Create new
    const createRes = await API.graphql(graphqlOperation(createCompanyProfile, {
      input: {
        email: demoEmail,
        companyName: "GetScreened Demo",
        companyUrl: "getscreened.ai",
        userID: "demo-admin",
        t_c: true
      }
    }));
    return createRes.data.createCompanyProfile.id;
  };

  const createDemoJD = async (companyId, role) => {
    const jdContent = getJDContent(role);
    
    const input = {
      companyProfileID: companyId,
      jobTitle: role,
      jobDescription: jdContent.description,
      skill: jdContent.skills.join(", "),
      selectedExperience: "0-2 years",
      selectedCategory: "Engineering",
      compensation: "Not Disclosed",
      benefits: "Standard Benefits",
      workMode: "Remote",
      jobIDNo: Math.floor(Math.random() * 10000)
    };

    const res = await API.graphql(graphqlOperation(createCompanyJobDescription, { input }));
    return res.data.createCompanyJobDescription.id;
  };

  const createDemoInterviewSlot = async (companyId, jdId, candidateProfileID, candidateName) => {
    const meetingConfig = createMeetingConfig({
      companyID: companyId,
      companyName: "GetScreened Demo",
      jobTitle: formData.role,
      jdID: jdId,
      interviewType: 'sign-in-less',
      candidateName: candidateName
    });

    // Add isUnregistered flag manually and set number of questions
    const configObj = JSON.parse(meetingConfig);
    configObj.isUnregistered = true;
    configObj.numberOfQuestions = 4; 

    const input = {
      candidateProfileID: candidateProfileID,
      candidateEmail: `demo-${Date.now()}@demo.getscreened.ai`,
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '10:00',
      status: 'ACTIVE',
      meetingLinkURL: '',
      meetingConfig: JSON.stringify(configObj),
      candidateAttendance: false,
      botAttendance: false,
      jdID: jdId,
      candidateProfileInterviewSlotsId: candidateProfileID
    };

    const res = await API.graphql(graphqlOperation(createInterviewSlot, { input }));
    return res.data.createInterviewSlot.id;
  };

  const getJDContent = (role) => {
    let specificSkills = [];
    let description = `This is a demo interview for the ${role} position.`;

    switch(role) {
      case "Frontend Developer":
        specificSkills = ["React", "JavaScript", "CSS", "HTML"];
        description += " We are looking for someone proficient in React and modern frontend technologies.";
        break;
      case "Backend Developer":
        specificSkills = ["Node.js", "Python", "Database Design", "API Development"];
        description += " We are looking for a backend developer with experience in scalable systems.";
        break;
      case "Full Stack Developer":
        specificSkills = ["React", "Node.js", "Database Design", "REST APIs"];
        description += " We are looking for a full stack developer with expertise across frontend and backend.";
        break;
      case "Data Scientist":
        specificSkills = ["Python", "Machine Learning", "SQL", "Data Analysis"];
        description += " We are looking for a data scientist to analyze complex datasets.";
        break;
      case "Product Manager":
        specificSkills = ["Product Strategy", "Market Analysis", "User Research", "Analytics"];
        description += " We are looking for a product manager with strong strategic thinking skills.";
        break;
      case "DevOps Engineer":
        specificSkills = ["Docker", "Kubernetes", "CI/CD", "Cloud Infrastructure"];
        description += " We are looking for a DevOps engineer with containerization and orchestration expertise.";
        break;
      default:
        specificSkills = ["General Technical Skills"];
    }

    return {
        description,
        skills: specificSkills
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <RiseLoader color={"#2563EB"} loading={true} size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 font-sans">
      <div className="mb-8">
        <img src="/logogetscreened-black.svg" alt="GetScreened Logo" className="h-14 invert brightness-0" />
      </div>

      <div className="max-w-4xl w-full bg-slate-800 rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row border border-slate-700">
        {/* Left Side: Create Account */}
        <div className="md:w-1/2 p-8 md:p-12 bg-blue-600 text-white flex flex-col justify-center items-center text-center">
          <h2 className="text-3xl font-bold mb-4">Full Experience</h2>
          <p className="mb-8 text-blue-100">
            Create an account to unlock full interview features, detailed reports, and save your progress.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-8 py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create Account / Login
          </button>
        </div>

        {/* Right Side: Demo Interview */}
        <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center bg-slate-800">
          {!showForm ? (
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4 text-white">Try a Demo</h2>
              <p className="mb-8 text-slate-400">
                Experience our AI interviewer with a quick 4-question session. No account required.
              </p>
              <button 
                onClick={() => setShowForm(true)}
                className="px-8 py-3 bg-slate-700 text-white rounded-full font-semibold hover:bg-slate-600 transition-colors shadow-lg border border-slate-600"
              >
                Start Demo Interview
              </button>
            </div>
          ) : (
            <div className="w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Demo Setup</h2>
                <button 
                  onClick={() => setShowForm(false)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleStartDemo} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-900 border-0 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-white placeholder-slate-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                  <div className="grid grid-cols-2 gap-2">
                    {roles.map(role => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => setFormData({...formData, role: role})}
                        className={`px-3 py-1 text-sm text-left rounded-lg border-0 transition-all ${
                          formData.role === role
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-slate-900 text-slate-400 hover:text-slate-300'
                        }`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-2 rounded">
                    {error}
                  </div>
                )}

                <button 
                  type="submit"
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-500 transition-colors shadow-md mt-4"
                >
                  Start Interview
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-8 text-slate-500 text-sm">
        Powered by GetScreened.ai
      </div>
    </div>
  );
};

export default DemoInterviewLanding;
